import React from "react";
import * as S from "../components/ui/Styled";
import { Header } from "../components/landing/Header";
import { Hero } from "../components/landing/Hero";
import { ProblemSolution } from "../components/landing/ProblemSolution";
import { Features } from "../components/landing/Features";
import { PwaSection } from "../components/landing/PwaSection";
import { HowItWorks } from "../components/landing/HowItWorks";
import { ComparisonTable } from "../components/landing/ComparisonTable";
import { Pricing } from "../components/landing/Pricing";
import { FAQ } from "../components/landing/FAQ";
import { FinalCTA } from "../components/landing/FinalCTA";
import { useTranslation } from "../hooks/useTranslation";
import { useSeo } from "../hooks/useSeo";
import { ROUTES_CONFIG } from "../config/routes";
import { FeedbackModal } from "../components/FeedbackModal";
import { useSchema } from "../hooks/useSchema";
import { landingSchema } from "../config/landingSchema";

interface LandingProps {
    onLaunch: () => void;
}

export const Landing: React.FC<LandingProps> = ({ onLaunch }) => {
    const { t, lang } = useTranslation();
    const [showFeedback, setShowFeedback] = React.useState(false);

    useSeo({
        title: t("landing.meta.title"),
        description: t("landing.meta.description"),
        // Aqui garantimos que o Google saiba qual versão está lendo
        canonicalUrl: `https://promptninja.solutionkit.com.br/${lang !== 'pt' ? `?lang=${lang}` : ''}`,
        ogType: 'website'
    });

    useSchema(landingSchema);

    React.useEffect(() => {
        if (window.location.hash === "#pricing") {
            const element = document.getElementById("pricing");
            if (element) {
                // Small delay to ensure rendering
                setTimeout(() => {
                    element.scrollIntoView({ behavior: "smooth" });
                }, 100);
            }
        }
    }, []);

    return (
        <S.LandingContainer>
            <Header />
            <Hero onLaunch={onLaunch} />
            <ProblemSolution />
            <HowItWorks onLaunch={onLaunch} />
            <Features />
            <Pricing onLaunch={onLaunch} />
            <ComparisonTable />
            <PwaSection />
            <FAQ />

            {/* Hub Guide & Music Mode Links */}
            <div className="bg-[#020617] border-slate-900 mb-16 text-center flex flex-col items-center gap-8">
                <div className="flex flex-col items-center">
                    <p className="text-slate-400 mb-4 text-sm uppercase tracking-widest font-bold">
                        {lang === "pt" ? "Para Músicos" :
                            lang === "es" ? "Para Músicos" :
                                "For Musicians"}
                    </p>
                    <a
                        href="https://music.solutionkit.com.br"
                        className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500 hover:brightness-110 transition-all border-b-2 border-amber-500/30 hover:border-amber-500 pb-1"
                    >
                        {lang === "pt" ? "🎸 Teleprompter Modo Músico" :
                            lang === "es" ? "🎸 Teleprompter Modo Músico" :
                                "🎸 Musician Mode Teleprompter"}
                    </a>
                </div>

                <div className="flex flex-col items-center opacity-70 hover:opacity-100 transition-opacity">
                    <p className="text-slate-400 mb-4 text-sm font-medium">
                        {lang === "pt" ? "Recursos Adicionais" :
                            lang === "es" ? "Recursos Adicionales" :
                                "Additional Resources"}
                    </p>
                    <a
                        href={
                            lang === "pt" ? ROUTES_CONFIG.SEO_HUB_GUIDE.paths.pt :
                                lang === "es" ? ROUTES_CONFIG.SEO_HUB_GUIDE.paths.es :
                                    ROUTES_CONFIG.SEO_HUB_GUIDE.paths.en
                        }
                        className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 hover:brightness-110 transition-all border-b border-purple-500/30 hover:border-purple-500 pb-1"
                    >
                        {lang === "pt" ? "📖 Guia Completo de Teleprompter" :
                            lang === "es" ? "📖 Guía Definitiva de Teleprompter" :
                                "📖 Ultimate Teleprompter Guide"}
                    </a>
                </div>
            </div>

            <FinalCTA onLaunch={onLaunch} />

            <footer className="py-12 border-t border-slate-900 bg-[#020617] text-slate-500 text-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12 text-center md:text-left w-full max-w-4xl">
                        <div>
                            <h4 className="font-bold text-white mb-4 uppercase tracking-wider text-xs">{t("footer.links.resources")}</h4>
                            <div className="flex flex-col gap-3">
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_O_QUE_E.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_O_QUE_E.paths.es : ROUTES_CONFIG.SEO_O_QUE_E.paths.en} className="hover:text-white transition-colors">
                                    {t("footer.links.whatIs")}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_ORATORIA.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_ORATORIA.paths.es : ROUTES_CONFIG.SEO_ORATORIA.paths.en} className="hover:text-white transition-colors">
                                    {t("footer.links.oratory")}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_CHECKLIST.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_CHECKLIST.paths.es : ROUTES_CONFIG.SEO_CHECKLIST.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Checklist de Gravação" : lang === "es" ? "Checklist de Grabación" : "Recording Checklist"}
                                </a>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold text-white mb-4 uppercase tracking-wider text-xs">{lang === "pt" ? "Tecnologia" : lang === "es" ? "Tecnología" : "Technology"}</h4>
                            <div className="flex flex-col gap-3">
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_ACCESSIBILITY.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_ACCESSIBILITY.paths.es : ROUTES_CONFIG.SEO_ACCESSIBILITY.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Acessibilidade" : lang === "es" ? "Accesibilidad" : "Accessibility"}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_FUTURE_AI.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_FUTURE_AI.paths.es : ROUTES_CONFIG.SEO_FUTURE_AI.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "IA e Futuro" : lang === "es" ? "IA y Futuro" : "AI and Future"}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_GRATIS.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_GRATIS.paths.es : ROUTES_CONFIG.SEO_GRATIS.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Teleprompter Grátis" : lang === "es" ? "Teleprompter Gratis" : "Free Teleprompter"}
                                </a>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold text-white mb-4 uppercase tracking-wider text-xs">{lang === "pt" ? "Soluções" : lang === "es" ? "Soluciones" : "Solutions"}</h4>
                            <div className="flex flex-col gap-3">
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_LIVES.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_LIVES.paths.es : ROUTES_CONFIG.SEO_LIVES.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Para Lives" : lang === "es" ? "Para Directos" : "For Live Streaming"}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_PODCAST.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_PODCAST.paths.es : ROUTES_CONFIG.SEO_PODCAST.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Para Podcasts" : lang === "es" ? "Para Podcasts" : "For Podcasts"}
                                </a>
                                <a href={lang === "pt" ? ROUTES_CONFIG.SEO_TABLET.paths.pt : lang === "es" ? ROUTES_CONFIG.SEO_TABLET.paths.es : ROUTES_CONFIG.SEO_TABLET.paths.en} className="hover:text-white transition-colors">
                                    {lang === "pt" ? "Teleprompter para Tablet" : lang === "es" ? "Teleprompter para Tablet" : "Teleprompter for Tablet"}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-center gap-4 mb-8">
                        <a href="mailto:teleprompterninja@gmail.com" className="text-slate-400 hover:text-brand-400 transition-colors font-medium">
                            teleprompterninja@gmail.com
                        </a>
                        <button
                            onClick={() => setShowFeedback(true)}
                            className="px-4 py-2 rounded-full border border-slate-800 hover:border-slate-600 text-slate-400 hover:text-white transition-all text-xs uppercase tracking-widest font-bold"
                        >
                            Feedback / Sugestões
                        </button>
                    </div>

                    <p>
                        &copy; {new Date().getFullYear()} {t("footer.copyright")}
                    </p>
                </div>
            </footer>

            <FeedbackModal show={showFeedback} onClose={() => setShowFeedback(false)} />
        </S.LandingContainer>
    );
};
